import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import authApi, { LoginCredentials } from '@/api/auth';
import { onboardingApi } from '@/api/onboarding';
import { setupApi } from '@/api/setup';
import { useTranslation } from '@/hooks/useTranslation';
import { PERMISSIONS } from '@/lib/permissions';

/**
 * Hook to access authentication state and methods
 * Provides login, logout, and auth checking functionality
 */
export function useAuth() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const {
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    requiresTwoFactor,
    requiresTwoFactorEnrollment,
    error,
    setUser,
    setLoading,
    setError,
    setOnboardingRequired,
    setSetupRequired,
    setTwoFactorRequirement,
  } = useAuthStore();

  /**
   * Shared post-verification routing: checks setup/onboarding status (only
   * reachable once the session is 2FA-verified — those endpoints are gated
   * too) and navigates with the same priority used everywhere in the app:
   * setup → onboarding → intended destination → dashboard.
   */
  const routeAfterVerifiedSession = useCallback(
    async (user: Awaited<ReturnType<typeof authApi.getCurrentUser>>['user'], redirectTo?: string) => {
      let needsSetup = false;
      let needsOnboarding = false;
      const hasSettingsPermission =
        user.permissions?.includes(PERMISSIONS.SETTINGS_UPDATE) || user.roles?.includes('super_admin');

      if (hasSettingsPermission) {
        try {
          const setupStatus = await setupApi.getStatus();
          needsSetup = !setupStatus.setup_complete;
          setSetupRequired(needsSetup);

          if (!needsSetup) {
            const onboardingStatus = await onboardingApi.getStatus();
            needsOnboarding = !onboardingStatus.completed;
            setOnboardingRequired(needsOnboarding);
          } else {
            setOnboardingRequired(false);
          }
        } catch {
          setSetupRequired(false);
          setOnboardingRequired(false);
        }
      }

      let destination: string;
      if (needsSetup) {
        destination = '/setup';
      } else if (needsOnboarding) {
        destination = '/onboarding';
      } else {
        destination = redirectTo || '/dashboard';
      }
      navigate(destination, { replace: true });
    },
    [navigate, setOnboardingRequired, setSetupRequired]
  );

  /**
   * Login with email and password
   * On success, sets user in store and redirects to intended URL, dashboard,
   * or — W1.2 — the 2FA challenge/enrollment screen if the session isn't
   * fully verified yet. Setup/onboarding status is only checked once the
   * session is 2FA-verified, since those endpoints are gated too.
   */
  const login = useCallback(
    async (credentials: LoginCredentials, redirectTo?: string) => {
      setLoading(true);
      setError(null);

      try {
        const session = await authApi.login(credentials);
        setUser(session.user);
        setTwoFactorRequirement(session.requiresTwoFactor, session.requiresTwoFactorEnrollment);

        if (session.requiresTwoFactorEnrollment) {
          navigate('/2fa/enroll', { replace: true });
          return;
        }
        if (session.requiresTwoFactor) {
          navigate('/2fa/challenge', { replace: true, state: { from: redirectTo ? { pathname: redirectTo } : undefined } });
          return;
        }

        await routeAfterVerifiedSession(session.user, redirectTo);
      } catch (err: unknown) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
          t('errors.invalid_credentials');
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [navigate, setUser, setLoading, setError, setTwoFactorRequirement, routeAfterVerifiedSession, t]
  );

  /**
   * W1.2 — call after a successful /2fa/challenge or /2fa/enroll/confirm:
   * clears the 2FA flags and runs the same post-verification routing as
   * login() (setup → onboarding → intended → dashboard).
   */
  const completeTwoFactor = useCallback(
    async (redirectTo?: string) => {
      setTwoFactorRequirement(false, false);
      const session = await authApi.getCurrentUser();
      setUser(session.user);
      await routeAfterVerifiedSession(session.user, redirectTo);
    },
    [setTwoFactorRequirement, setUser, routeAfterVerifiedSession]
  );

  /**
   * Logout current user
   * Clears session, local state, and redirects to login
   */
  const logout = useCallback(async () => {
    setLoading(true);

    try {
      await authApi.logout();
    } catch (err) {
      // Log error but continue with logout
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setTwoFactorRequirement(false, false);
      setLoading(false);
      navigate('/login', { replace: true });
    }
  }, [navigate, setUser, setLoading, setTwoFactorRequirement]);

  /**
   * Check if user is authenticated by fetching current user
   * Useful for verifying session on app load
   */
  const checkAuth = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const session = await authApi.checkAuth();
      setUser(session?.user ?? null);
      setTwoFactorRequirement(
        session?.requiresTwoFactor ?? false,
        session?.requiresTwoFactorEnrollment ?? false
      );
      return !!session?.user;
    } catch (err) {
      setUser(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading, setError, setTwoFactorRequirement]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    user,
    isAuthenticated,
    isLoading,
    isInitialized,
    requiresTwoFactor,
    requiresTwoFactorEnrollment,
    error,
    login,
    logout,
    checkAuth,
    completeTwoFactor,
    clearError,
  };
}
